import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useParams, useSearchParams } from 'next/navigation';
import SidebarContent from './SidebarContent';
import { useAtom } from 'jotai';
import { contentAtom, desktopSidebar, mapViewAtom } from '../../jotaiStore';
import { User } from '@supabase/supabase-js';
import { useHydrateAtoms } from 'jotai/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import LoadingFull from '../global/loading/LoadingFull';

const DesktopSidebarContainer: React.FC<{
  user: User | null;
  children: React.ReactNode;
  setShowLoadingContent: (showLoadingContent: boolean) => void;
  showLoadingContent: boolean;
  graphID: string;
  flow: React.ReactNode;
}> = ({ user, children, setShowLoadingContent, showLoadingContent, graphID, flow }) => {
  useHydrateAtoms([[desktopSidebar, false]]);
  const [showDesktopSidebar, setShowDesktopSidebar] = useAtom(desktopSidebar);
  const [showMapView] = useAtom(mapViewAtom);
  const [content] = useAtom(contentAtom);
  const lastScrollPosition = useRef(0);

  //check if there is a ?flow=true in the url
  const params = useParams();
  const showFlow = useSearchParams().get('flow');
  
  const contentSlug = params?.contentSlug as string;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollbarStyle =
    'scrollbar scrollbar-thumb-shades-surfaceDark scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-shades-base scrollbar-w-2';

  return (
    <div className="w-[100vw] flex min-w-screen relative">
      <AnimatePresence>
        {/* Left Sidebar */}
        <motion.div
          initial={{ x: showDesktopSidebar ? '0%' : '-100%' }}
          animate={{ x: showDesktopSidebar ? '0%' : '-100%' }}
          exit={{ x: '-100%' }}
          transition={{ duration: 0.2 }}
          // @ts-ignore
          className={cn(
            'overflow-x-hidden hidden min-[1001px]:block fixed w-[40%] max-w-[600px] min-w-[350px] inset-y-0 z-50 flex-col gap-y-5 bg-shades-base overflow-y-auto h-[calc(100vh-3.5rem)] border-r-[1px] border-shades-surfaceDark mt-[55px]',
            scrollbarStyle
          )}
          key="sidebar-content"
        >
          <SidebarContent
            user={user}
            setShowLoadingContent={setShowLoadingContent}
            graphID={graphID}
          >
            {flow}
          </SidebarContent>
        </motion.div>

        {/* Main Content */}
        <motion.main
          // @ts-ignore
          className={clsx(
            'hidden min-[1001px]:block h-full grow relative',
            contentSlug === 'overview'
              ? 'max-h-[calc(100vh-55px)]'
              : 'max-h-[calc(100vh-55px)]'
          )}
          initial={{
            marginLeft: showDesktopSidebar ? 'min(40%, 600px)' : '0px',
          }}
          animate={{
            marginLeft: showDesktopSidebar ? 'min(40%, 600px)' : '0px',
          }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          key="main-content"
        >
          {/* {(showLoadingContent || !children || !content) && (
            <div className="w-full min-h-[calc(100vh-55px)]">
              <LoadingFull />
            </div>
          )} */}
          {children || null}
        </motion.main>
      </AnimatePresence>

      {/* Mobile view */}
      <div className="min-[1001px]:hidden w-full">
        {showLoadingContent || !children || !content ? (
          <div className="w-full min-h-[calc(100vh-55px)] absolute">
            <LoadingFull />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export default DesktopSidebarContainer;
